// src/admin/pages/Events.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

import { apiFetch } from '../../utils/api';

const EVENTS_ENDPOINT = '/api/admin/events';

function formatDate(ymd) {
    if (!ymd) return '';
    const [y, m, d] = String(ymd).split('-').map(Number);
    if (!y || !m || !d) return ymd;
    const dt = new Date(y, m - 1, d);
    if (Number.isNaN(dt.getTime())) return ymd;
    return dt.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    });
}

function formatTime(t) {
    if (!t) return '';
    const [hStr, mStr] = String(t).split(':');
    const h = Number(hStr);
    const m = Number(mStr || 0);
    if (Number.isNaN(h) || Number.isNaN(m)) return t;
    const dt = new Date();
    dt.setHours(h, m, 0, 0);
    return dt.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
    });
}

function EventFormDialog({ open, eventId, mode, onClose, onSaved }) {
    const isEdit = mode === 'edit';

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [fields, setFields] = useState({
        title: '',
        summary: '',
        description: '',
        event_date: '',
        start_time: '',
        end_time: '',
        location: '',
    });

    const [existingPhotos, setExistingPhotos] = useState([]); // [{id,url,is_cover,sort_order}]
    const [removedPhotoIds, setRemovedPhotoIds] = useState(() => new Set());
    const [newFiles, setNewFiles] = useState([]); // [File]
    const [newPreviews, setNewPreviews] = useState([]); // [blobUrl]

    useEffect(() => {
        if (!open) return;
        setError('');
        setSaving(false);
        setRemovedPhotoIds(new Set());
        setNewFiles([]);
        setNewPreviews([]);

        if (isEdit && eventId) {
            (async () => {
                setLoading(true);
                try {
                    const res = await apiFetch(`${EVENTS_ENDPOINT}/${eventId}`, {
                        credentials: 'include',
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) {
                        throw new Error(data.error || 'Failed to load event.');
                    }
                    const { event, photos } = data;
                    setFields({
                        title: event.title || '',
                        summary: event.summary || '',
                        description: event.description || '',
                        event_date: event.event_date || '',
                        start_time: event.start_time || '',
                        end_time: event.end_time || '',
                        location: event.location || '',
                    });
                    setExistingPhotos(
                        Array.isArray(photos)
                            ? photos.map((p) => ({
                                id: p.id,
                                url: p.url,
                                is_cover: !!p.is_cover,
                                sort_order: p.sort_order ?? 0,
                            }))
                            : []
                    );
                } catch (err) {
                    console.error(err);
                    setError(err.message || 'Failed to load event.');
                } finally {
                    setLoading(false);
                }
            })();
        } else {
            const today = new Date().toISOString().slice(0, 10);
            setFields((prev) => ({
                ...prev,
                title: '',
                summary: '',
                description: '',
                event_date: today,
                start_time: '',
                end_time: '',
                location: '',
            }));
            setExistingPhotos([]);
        }
    }, [open, isEdit, eventId]);

    useEffect(() => {
        return () => {
            newPreviews.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [newPreviews]);

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFields((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const previews = files.map((file) => URL.createObjectURL(file));
        setNewFiles((prev) => [...prev, ...files]);
        setNewPreviews((prev) => [...prev, ...previews]);

        e.target.value = '';
    };

    const handleRemoveExisting = (photoId) => {
        setRemovedPhotoIds((prev) => {
            const next = new Set(prev);
            next.add(photoId);
            return next;
        });
    };

    const handleRemoveNew = (index) => {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
        setNewPreviews((prev) => {
            const copy = [...prev];
            const [removed] = copy.splice(index, 1);
            if (removed) URL.revokeObjectURL(removed);
            return copy;
        });
    };

    const visibleExistingPhotos = useMemo(
        () => existingPhotos.filter((p) => !removedPhotoIds.has(p.id)),
        [existingPhotos, removedPhotoIds]
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            if (!fields.title.trim()) {
                throw new Error('Title is required.');
            }
            if (!fields.event_date) {
                throw new Error('Event date is required.');
            }

            const fd = new FormData();
            fd.append('title', fields.title.trim());
            if (fields.summary) fd.append('summary', fields.summary);
            if (fields.description) fd.append('description', fields.description);
            if (fields.event_date) fd.append('event_date', fields.event_date);
            if (fields.start_time) fd.append('start_time', fields.start_time);
            if (fields.end_time) fd.append('end_time', fields.end_time);
            if (fields.location) fd.append('location', fields.location);

            if (removedPhotoIds.size) {
                fd.append(
                    'remove_photo_ids',
                    JSON.stringify(Array.from(removedPhotoIds))
                );
            }

            newFiles.forEach((file) => {
                fd.append('images', file);
            });

            const url = isEdit
                ? `${EVENTS_ENDPOINT}/${eventId}`
                : EVENTS_ENDPOINT;

            const res = await apiFetch(url, {
                method: isEdit ? 'PUT' : 'POST',
                credentials: 'include',
                body: fd,
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'Failed to save event.');
            }

            onSaved(isEdit ? 'Event updated.' : 'Event created.');
            onClose();
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to save event.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                {isEdit ? 'Edit event' : 'Create event'}
                <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Typography variant="body2">Loading…</Typography>
                ) : (
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                        {error && (
                            <Alert
                                severity="error"
                                onClose={() => setError('')}
                                sx={{ mb: 1 }}
                            >
                                {error}
                            </Alert>
                        )}

                        <TextField
                            label="Title"
                            name="title"
                            fullWidth
                            required
                            value={fields.title}
                            onChange={handleFieldChange}
                        />
                        <TextField
                            label="Short summary (optional)"
                            name="summary"
                            fullWidth
                            value={fields.summary}
                            onChange={handleFieldChange}
                        />
                        <TextField
                            label="Description"
                            name="description"
                            fullWidth
                            multiline
                            minRows={3}
                            value={fields.description}
                            onChange={handleFieldChange}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Event date"
                                name="event_date"
                                type="date"
                                required
                                value={fields.event_date}
                                onChange={handleFieldChange}
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="Start time"
                                name="start_time"
                                type="time"
                                value={fields.start_time || ''}
                                onChange={handleFieldChange}
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="End time"
                                name="end_time"
                                type="time"
                                value={fields.end_time || ''}
                                onChange={handleFieldChange}
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1 }}
                            />
                        </Stack>

                        <TextField
                            label="Location"
                            name="location"
                            fullWidth
                            value={fields.location}
                            onChange={handleFieldChange}
                        />

                        {/* Photos section */}
                        <Box sx={{ mt: 2 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, mb: 1 }}
                            >
                                Photos (optional)
                            </Typography>
                            <Button variant="outlined" component="label" size="small">
                                Add photos
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={handleFileChange}
                                />
                            </Button>

                            <Box
                                sx={{
                                    mt: 1.5,
                                    maxHeight: 220,
                                    borderRadius: 1,
                                    border: '1px solid rgba(0,0,0,0.12)',
                                    overflowY: 'auto',
                                    p: 1,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {visibleExistingPhotos.map((p) => (
                                    <Box
                                        key={p.id}
                                        sx={{
                                            position: 'relative',
                                            width: 96,
                                            height: 72,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            border: '1px solid rgba(0,0,0,0.2)',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={p.url}
                                            alt=""
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveExisting(p.id)}
                                            sx={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 2,
                                                bgcolor: 'rgba(0,0,0,0.6)',
                                                color: 'white',
                                                '&:hover': {
                                                    bgcolor: 'rgba(0,0,0,0.8)',
                                                },
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                ))}

                                {newPreviews.map((url, index) => (
                                    <Box
                                        key={`new-${index}`}
                                        sx={{
                                            position: 'relative',
                                            width: 96,
                                            height: 72,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            border: '1px solid rgba(0,0,0,0.2)',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={url}
                                            alt=""
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveNew(index)}
                                            sx={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 2,
                                                bgcolor: 'rgba(0,0,0,0.6)',
                                                color: 'white',
                                                '&:hover': {
                                                    bgcolor: 'rgba(0,0,0,0.8)',
                                                },
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                ))}

                                {!visibleExistingPhotos.length &&
                                    !newPreviews.length && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ my: 1, px: 0.5 }}
                                        >
                                            No photos added yet.
                                        </Typography>
                                    )}
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={saving || loading}
                >
                    {isEdit ? 'Save changes' : 'Create event'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function EventsAdminPage() {
    const [tab, setTab] = useState('upcoming');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [banner, setBanner] = useState(null); // {type, message}
    const [confirm, setConfirm] = useState({
        open: false,
        id: null,
        title: '',
    });

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            params.set('tab', tab);
            const res = await apiFetch(
                `${EVENTS_ENDPOINT}?${params.toString()}`,
                {
                    credentials: 'include',
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'Failed to load events.');
            }
            setRows(Array.isArray(data.rows) ? data.rows : []);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to load events.');
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const openCreate = () => {
        setEditingId(null);
        setFormOpen(true);
    };

    const openEdit = (id) => {
        setEditingId(id);
        setFormOpen(true);
    };

    const handleSaved = (msg) => {
        setBanner({ type: 'success', message: msg });
        fetchEvents();
    };

    const handleDelete = async () => {
        if (!confirm.id) return;
        try {
            const res = await apiFetch(`${EVENTS_ENDPOINT}/${confirm.id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'Failed to delete event.');
            }
            setBanner({ type: 'success', message: 'Event deleted.' });
            setConfirm({ open: false, id: null, title: '' });
            fetchEvents();
        } catch (err) {
            console.error(err);
            setBanner({
                type: 'error',
                message: err.message || 'Failed to delete event.',
            });
            setConfirm({ open: false, id: null, title: '' });
        }
    };

    const titleForTab =
        tab === 'upcoming' ? 'Upcoming events' : 'Previous events';

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
                flexWrap="wrap"
                rowGap={1}
            >
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Events
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreate}
                    sx={{ mt: { xs: 1, sm: 0 } }}
                >
                    Create event
                </Button>
            </Stack>

            {banner && (
                <Box sx={{ mb: 2 }}>
                    <Alert
                        severity={banner.type}
                        onClose={() => setBanner(null)}
                    >
                        {banner.message}
                    </Alert>
                </Box>
            )}

            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={tab}
                    onChange={(_e, v) => setTab(v)}
                    aria-label="Event tabs"
                    variant="scrollable"
                >
                    <Tab label="Upcoming events" value="upcoming" />
                    <Tab label="Previous events" value="previous" />
                </Tabs>

                <Box sx={{ p: 2 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 1.5 }}
                    >
                        {titleForTab}
                    </Typography>

                    {loading ? (
                        <Typography variant="body2">Loading…</Typography>
                    ) : error ? (
                        <Alert
                            severity="error"
                            onClose={() => setError('')}
                            sx={{ mb: 1 }}
                        >
                            {error}
                        </Alert>
                    ) : !rows.length ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            No events found for this tab yet.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Date</TableCell>
                                        {/* Time column: non-clickable */}
                                        <TableCell>Time</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell align="right">
                                            Photos
                                        </TableCell>
                                        <TableCell align="right">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => {
                                        const timeStr =
                                            row.start_time || row.end_time
                                                ? `${row.start_time ? formatTime(row.start_time) : ''}${
                                                    row.start_time &&
                                                    row.end_time
                                                        ? ' – '
                                                        : ''
                                                }${
                                                    row.end_time
                                                        ? formatTime(
                                                            row.end_time
                                                        )
                                                        : ''
                                                }`
                                                : '';

                                        return (
                                            <TableRow
                                                key={row.id}
                                                hover
                                                sx={{ cursor: 'default' }}
                                            >
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {row.title}
                                                    </Typography>
                                                    {row.summary && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                mt: 0.25,
                                                                maxWidth: 420,
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                display:
                                                                    '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient:
                                                                    'vertical',
                                                            }}
                                                        >
                                                            {row.summary}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(
                                                        row.event_date
                                                    )}
                                                </TableCell>
                                                {/* Time cell: plain text or non-interactive pill */}
                                                <TableCell>
                                                    {timeStr ? (
                                                        timeStr
                                                    ) : (
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                display:
                                                                    'inline-flex',
                                                                alignItems:
                                                                    'center',
                                                                px: 1.2,
                                                                py: 0.25,
                                                                borderRadius:
                                                                    9999,
                                                                fontSize:
                                                                    '0.75rem',
                                                                lineHeight: 1.5,
                                                                bgcolor:
                                                                    'grey.200',
                                                                color:
                                                                    'text.secondary',
                                                                pointerEvents:
                                                                    'none',
                                                            }}
                                                        >
                                                            TBA
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {row.location || '—'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row.photos_count || 0}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Edit event"
                                                        onClick={() =>
                                                            openEdit(row.id)
                                                        }
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Delete event"
                                                        onClick={() =>
                                                            setConfirm({
                                                                open: true,
                                                                id: row.id,
                                                                title: row.title,
                                                            })
                                                        }
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Paper>

            <EventFormDialog
                open={formOpen}
                eventId={editingId}
                mode={editingId ? 'edit' : 'create'}
                onClose={() => setFormOpen(false)}
                onSaved={handleSaved}
            />

            {/* Delete confirm dialog */}
            <Dialog
                open={confirm.open}
                onClose={() =>
                    setConfirm({ open: false, id: null, title: '' })
                }
            >
                <DialogTitle>Delete event</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Are you sure you want to delete "
                        <strong>{confirm.title}</strong>"? This will remove the
                        event and its photos from the admin console and public
                        site. (Existing files in Google Cloud Storage will not
                        be deleted.)
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setConfirm({ open: false, id: null, title: '' })
                        }
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
